import { useMemo, useState } from 'react';
import { BlockBuilder } from './blocks/BlockBuilder';
import type { BlockLine } from '../types/blocks';

export type ConnectSlideId = 'constant-ratio' | 'for-every' | 'part-to-whole';

export interface ConnectSlideDescriptor {
  id: ConnectSlideId;
  title: string;
}

export const CONNECT_SLIDES: ConnectSlideDescriptor[] = [
  { id: 'for-every', title: 'For Every' },
  { id: 'constant-ratio', title: 'Constant Ratios' },
  { id: 'part-to-whole', title: 'Part to Whole' },
];

interface ConnectInnerNavProps {
  currentSlideIndex: number;
  viewedSlideIds: ConnectSlideId[];
  onSelectSlide: (index: number) => void;
}

export function ConnectInnerNav({ currentSlideIndex, viewedSlideIds, onSelectSlide }: ConnectInnerNavProps) {
  return (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-p-1">
      {CONNECT_SLIDES.map((slide, index) => {
        const isSelected = index === currentSlideIndex;
        const isViewed = viewedSlideIds.includes(slide.id);
        return (
          <button
            key={slide.id}
            type="button"
            onClick={() => onSelectSlide(index)}
            className="tw-w-full tw-rounded-lg tw-border tw-text-left tw-px-3 tw-py-2 tw-transition-colors"
            style={{
              borderColor: isSelected ? 'var(--primary)' : 'var(--border)',
              background: isSelected ? 'color-mix(in oklab, var(--primary) 10%, var(--card))' : 'var(--card)',
              color: 'var(--foreground)',
            }}
          >
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
              <span className="tw-text-sm tw-font-medium">{slide.title}</span>
              {isViewed ? <span className="tw-text-xs tw-text-neutral-700">✓</span> : null}
            </div>
          </button>
        );
      })}
    </div>
  );
}

interface ConnectSlidesProps {
  currentSlideIndex: number;
  onSelectSlide: (index: number) => void;
  onComplete: () => void;
  completionTargetLabel: 'Practice' | 'Explore';
}

export function ConnectSlides({
  currentSlideIndex,
  onSelectSlide,
  onComplete,
  completionTargetLabel,
}: ConnectSlidesProps) {
  const MIN_COUNT = 1;
  const MAX_COUNT = 10;
  const [bikeCount, setBikeCount] = useState(2);
  const [sodaCanCount, setSodaCanCount] = useState(2);
  const [airRatioMultiple, setAirRatioMultiple] = useState(1);

  const canGoPrev = currentSlideIndex > 0;
  const canGoNext = currentSlideIndex < CONNECT_SLIDES.length - 1;

  const slideContent = useMemo(() => {
    if (currentSlideIndex === 0) {
      const totalCaffeine = sodaCanCount * 35;
      return (
        <div className="tw-flex tw-flex-col tw-gap-4">
          <p className="tw-m-0 tw-max-w-3xl tw-mx-auto tw-leading-relaxed tw-text-base">
            Ratios are &quot;for every&quot; relationships. For every bike there are two tires. For every can of cola,
            there are about 35 milligrams of caffeine.
          </p>
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
            <button
              type="button"
              onClick={() => setSodaCanCount(value => Math.max(MIN_COUNT, value - 1))}
              disabled={sodaCanCount <= MIN_COUNT}
              className="tw-rounded-md tw-border tw-px-2 tw-py-1 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
            >
              -
            </button>
            <span className="tw-text-sm tw-font-medium">Cans of cola: {sodaCanCount}</span>
            <button
              type="button"
              onClick={() => setSodaCanCount(value => Math.min(MAX_COUNT, value + 1))}
              disabled={sodaCanCount >= MAX_COUNT}
              className="tw-rounded-md tw-border tw-px-2 tw-py-1 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
            >
              +
            </button>
          </div>
          <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-3">
            {Array.from({ length: sodaCanCount }, (_, index) => (
              <div
                key={`cola-${index}`}
                className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-rounded-md tw-border tw-p-2"
              >
                <span className="tw-text-2xl">🥤</span>
                <span className="tw-text-xs tw-bg-yellow-100 tw-rounded-full tw-px-2 tw-py-0.5">35 mg caffeine</span>
              </div>
            ))}
          </div>
          <div className="tw-text-center tw-text-sm tw-text-neutral-700">
            Total caffeine represented: {totalCaffeine} mg
          </div>
        </div>
      );
    }

    if (currentSlideIndex === 1) {
      const tireCount = bikeCount * 2;
      return (
        <div className="tw-flex tw-flex-col tw-gap-4">
          <p className="tw-m-0 tw-max-w-3xl tw-mx-auto tw-leading-relaxed tw-text-base">
            &quot;Ratios&quot; are relationships where the result of dividing one quantity by the other is always the
            same, no matter how much of it you have. For example, dividing bikes by tires is always 1/2. (Unless
            you&apos;re missing a tire!)
          </p>
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
            <button
              type="button"
              onClick={() => setBikeCount(value => Math.max(MIN_COUNT, value - 1))}
              disabled={bikeCount <= MIN_COUNT}
              className="tw-rounded-md tw-border tw-px-2 tw-py-1 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
            >
              -
            </button>
            <span className="tw-text-sm tw-font-medium">Bikes: {bikeCount}</span>
            <button
              type="button"
              onClick={() => setBikeCount(value => Math.min(MAX_COUNT, value + 1))}
              disabled={bikeCount >= MAX_COUNT}
              className="tw-rounded-md tw-border tw-px-2 tw-py-1 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
            >
              +
            </button>
          </div>
          <div className="tw-mx-auto tw-w-full tw-max-w-3xl tw-rounded-lg tw-border tw-p-3">
            <div className="tw-text-center tw-text-sm tw-font-medium tw-mb-2">Bike to tire representation</div>
            <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-3">
              {Array.from({ length: bikeCount }, (_, index) => (
                <div
                  key={`bike-${index}`}
                  className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-rounded-md tw-border tw-p-2"
                >
                  <span className="tw-text-2xl">🚲</span>
                </div>
              ))}
            </div>
            <div className="tw-text-center tw-text-sm tw-text-neutral-600 tw-mt-2">
              Tires needed: {tireCount} (ratio {bikeCount}:{tireCount}
              {bikeCount > 1 ? ', equivalent to 1:2' : ''})
            </div>
          </div>
        </div>
      );
    }

    const oxygenTapeLines: BlockLine[] = [
      { id: 'oxygen', blocks: 21, color: '#38bdf8', label: 'Oxygen' },
      { id: 'not-oxygen', blocks: 79, color: '#94a3b8', label: 'Not oxygen' },
    ];
    const totalOxygen = 21 * airRatioMultiple;
    const totalNotOxygen = 79 * airRatioMultiple;
    const totalAir = 100 * airRatioMultiple;

    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <p className="tw-m-0 tw-max-w-3xl tw-mx-auto tw-leading-relaxed tw-text-base">
          Sometimes we compare two separate quantities (called &quot;part to part&quot;), but sometimes we compare a
          quantity to a group that contains the quantity (called &quot;part to whole&quot;). For example, for every 21
          liters of oxygen in the atmosphere, there are 100 liters of air total.
        </p>
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
          <button
            type="button"
            onClick={() => setAirRatioMultiple(value => Math.max(1, value - 1))}
            disabled={airRatioMultiple <= 1}
            className="tw-rounded-md tw-border tw-px-2 tw-py-1 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
          >
            -
          </button>
          <span className="tw-text-sm tw-font-medium">Number of groups: {airRatioMultiple}</span>
          <button
            type="button"
            onClick={() => setAirRatioMultiple(value => Math.min(10, value + 1))}
            disabled={airRatioMultiple >= 10}
            className="tw-rounded-md tw-border tw-px-2 tw-py-1 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
          >
            +
          </button>
        </div>
        <div className="tw-mx-auto tw-max-w-3xl tw-text-center tw-text-sm tw-font-medium tw-mb-2">
          Part-to-whole air model
        </div>
        <BlockBuilder
          achievementId={null}
          currentLines={oxygenTapeLines}
          currentArrangement="sequence"
          currentMultiplier={airRatioMultiple}
          displayOnly
          hideLabels
          displayLines={oxygenTapeLines}
          displayArrangement="sequence"
          displayMultiplier={airRatioMultiple}
          autoHeight
          maxHeight={360}
        />
        <div className="tw-mx-auto tw-mt-2 tw-grid tw-w-full tw-max-w-3xl tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-2 tw-text-xs tw-text-neutral-700">
          <div className="tw-rounded-md tw-bg-sky-50 tw-p-2">
            Each group: 21 L oxygen + 79 L not oxygen = 100 L air.
          </div>
          <div className="tw-rounded-md tw-bg-slate-50 tw-p-2">
            With {airRatioMultiple} group{airRatioMultiple > 1 ? 's' : ''}: oxygen {totalOxygen} L, not oxygen{' '}
            {totalNotOxygen} L, total air {totalAir} L.
          </div>
        </div>
        <div className="tw-mx-auto tw-mt-2 tw-w-full tw-max-w-3xl tw-rounded-md tw-border tw-border-amber-300 tw-bg-amber-50 tw-p-2 tw-text-center tw-text-xs tw-text-amber-900">
          Be careful! Check ratios to see if they&apos;re between two parts, or a part and the whole group!
        </div>
      </div>
    );
  }, [airRatioMultiple, bikeCount, currentSlideIndex, sodaCanCount]);

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-rounded-2xl tw-bg-white tw-shadow-2xl">
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-5 tw-overflow-auto tw-p-6">
        <div className="tw-grid tw-grid-cols-1 tw-gap-2 md:tw-grid-cols-[1fr_auto_1fr] md:tw-gap-0">
          <h2 className="tw-m-0 tw-max-w-3xl tw-mx-auto tw-font-atkinson tw-text-2xl md:tw-col-start-2 md:tw-justify-self-center">
            {CONNECT_SLIDES[currentSlideIndex].title}
          </h2>
          <span className="tw-text-sm tw-text-neutral-500 md:tw-col-start-3 md:tw-justify-self-end">
            Slide {currentSlideIndex + 1} of {CONNECT_SLIDES.length}
          </span>
        </div>
        {slideContent}
        <div className="tw-mt-auto tw-flex tw-justify-between tw-pt-2">
          <button
            type="button"
            disabled={!canGoPrev}
            onClick={() => onSelectSlide(currentSlideIndex - 1)}
            className="tw-rounded-md tw-border tw-px-3 tw-py-1.5 tw-cursor-pointer tw-transition-colors hover:tw-bg-neutral-100 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={false}
            onClick={() => (canGoNext ? onSelectSlide(currentSlideIndex + 1) : onComplete())}
            className="tw-rounded-md tw-border tw-border-green-600 tw-bg-green-600 tw-text-white tw-px-3 tw-py-1.5 tw-cursor-pointer tw-transition-colors hover:tw-bg-green-700 hover:tw-border-green-700 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
          >
            {canGoNext ? 'Next' : `Go to ${completionTargetLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}
